import { Chip } from '@progress/kendo-react-buttons';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { ActionRequiredColumns } from 'src/assets/static/action_required_invoices/ActionRequiredColumns';
import GridWrapper from 'src/components/GridWrapper';
import useActionRequiredInvoices from 'src/hooks/useActionRequiredInvoices';
import { IItemsActionRequired } from 'src/services/useActionRequiredInvoicesApi';

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};

const defaultProps = {
  DataItemKey: 'rowId',
  tableTextId: 'documents',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  pageable: gridPagerSettings,
} as const;

const ActionRequiredGrid = () => {
  const {
    formatedActionRequiredItems,
    totalActionRequiredInvoices,
    actionRequiredLoading,
    setSelectedInvoices,
    actionRequiredDataState,
    setActionRequiredDataState,
    setActionRequiredSelectionFilter,
  } = useActionRequiredInvoices();
  const [rows, setRows] = useState<IItemsActionRequired[]>(formatedActionRequiredItems);

  const onInvoiceDataStateChange = (e: GridDataStateChangeEvent) => {
    if (e.dataState?.sort?.[0]?.field !== 'glSummaries') setActionRequiredDataState(e.dataState);
  };

  const onInvoiceSelect = React.useCallback((event: GridSelectionChangeEvent) => {
    setRows((prev) => {
      const prevItems = [...prev];
      const index = event?.startRowIndex;
      prevItems[index].selected = !prevItems[index]?.selected;
      return prevItems;
    });
  }, []);

  const handleClearFilter = useCallback(() => {
    setActionRequiredSelectionFilter({
      value: 0,
      label: 'Show All',
    });
    setActionRequiredDataState({
      skip: 0,
      take: 50,
      sort: [{ field: 'invoiceDate', dir: 'asc' }],
      filter: {
        logic: 'and',
        filters: [],
      },
    });
  }, [setActionRequiredDataState, setActionRequiredSelectionFilter]);

  const onHeaderSelectionChange = React.useCallback((event: GridHeaderSelectionChangeEvent) => {
    const checkboxElement: any = event?.syntheticEvent?.target;
    const { checked } = checkboxElement;
    setRows((prev) => prev?.map((row) => ({ ...row, selected: checked })));
  }, []);

  useEffect(() => {
    const selected = rows.filter((row) => row.selected);

    setSelectedInvoices((prevSelected) => {
      if (JSON.stringify(prevSelected) !== JSON.stringify(selected)) return selected;
      return prevSelected;
    });
  }, [rows, setSelectedInvoices]);

  useEffect(() => {
    setRows(formatedActionRequiredItems);
  }, [formatedActionRequiredItems]);

  return (
    <div className="actionRequiredGridCover flex flex-col">
      <Chip
        size={'small'}
        className="mb-1 w-fit"
        rounded={'large'}
        removable
        text="Clear Filters"
        onClick={handleClearFilter}
      />
      <GridWrapper
        columns={ActionRequiredColumns}
        dataSource={rows}
        total={totalActionRequiredInvoices}
        hideScrollBar
        className="actionRequiredGrid"
        onSelectionChange={onInvoiceSelect}
        onHeaderSelectionChange={onHeaderSelectionChange}
        loading={actionRequiredLoading}
        dataState={actionRequiredDataState}
        onDataStateChange={onInvoiceDataStateChange}
        {...defaultProps}
      />
    </div>
  );
};

export default memo(ActionRequiredGrid);
